/**
 * Systeem Check — Proactieve diagnostiek voor beheerders
 * Controleert data-integriteit, configuratie, beveiliging, beschikbaarheid,
 * performance en geeft suggesties. Auto-fix knoppen voor veelvoorkomende problemen.
 * AI-analyse export voor uitgebreid advies met nieuwste tools.
 */
import React, { useState, useCallback, useRef } from 'react';
import { useApp } from '../contexts/AppContext';
import { isGehasht, hashWachtwoord } from '../utils/wachtwoord';
import { getOutlookStatusSync } from '../services/calendarService';
import { getBeschikbaarheidAllemaal, getWerkUren, checkCapaciteit } from '../services/beschikbaarheidService';

const SEVERITY = { error: '🔴', warning: '🟡', info: '🔵', ok: '🟢' };
const SEV_BG = { error: '#fde7e9', warning: '#fff4ce', info: '#e8f4fd', ok: '#dff6dd' };
const SEV_COLOR = { error: '#D13438', warning: '#7A6400', info: '#0078D4', ok: '#107C10' };

function generateFeestdagen(jaar) {
  const a = jaar % 19, b = Math.floor(jaar / 100), c = jaar % 100;
  const d = Math.floor(b / 4), e = b % 4, f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3), h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4), k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const maand = Math.floor((h + l - 7 * m + 114) / 31);
  const dag = ((h + l - 7 * m + 114) % 31) + 1;
  const pasen = new Date(jaar, maand - 1, dag);
  const addDays = (d, n) => { const r = new Date(d); r.setDate(r.getDate() + n); return r; };
  const fmt = d => d.toISOString().split('T')[0];
  return [
    { naam: 'Nieuwjaarsdag', datum: `${jaar}-01-01` },
    { naam: 'Goede Vrijdag', datum: fmt(addDays(pasen, -2)) },
    { naam: '1e Paasdag', datum: fmt(pasen) },
    { naam: '2e Paasdag', datum: fmt(addDays(pasen, 1)) },
    { naam: 'Koningsdag', datum: `${jaar}-04-27` },
    { naam: 'Bevrijdingsdag', datum: `${jaar}-05-05` },
    { naam: 'Hemelvaartsdag', datum: fmt(addDays(pasen, 39)) },
    { naam: '1e Pinksterdag', datum: fmt(addDays(pasen, 49)) },
    { naam: '2e Pinksterdag', datum: fmt(addDays(pasen, 50)) },
    { naam: '1e Kerstdag', datum: `${jaar}-12-25` },
    { naam: '2e Kerstdag', datum: `${jaar}-12-26` },
  ];
}

export default function SystemCheck({ onNavigate }) {
  const {
    medewerkers, verlofaanvragen, overuren, feestdagen, vakantiePerioden,
    roosterWijzigingen, jaararchief, verlofTypes, flexUren, flexPlanning,
    flexUitbetalingen, notificaties, menuConfig,
    werkMedewerkerBij, voegFeestdagToe, markeerAllesGelezen
  } = useApp();
  const [results, setResults] = useState(null);
  const [running, setRunning] = useState(false);
  const [fixing, setFixing] = useState(null);
  const [expandedCat, setExpandedCat] = useState({});
  const [lastRun, setLastRun] = useState(null);
  const [showAiPanel, setShowAiPanel] = useState(false);
  const [copied, setCopied] = useState(false);

  const runCheck = useCallback(async () => {
    setRunning(true);
    const checks = [];
    const now = new Date();
    const currentYear = now.getFullYear();
    const today = now.toISOString().split('T')[0];
    const actieveMw = medewerkers.filter(m => (m.Status || 'actief') === 'actief');
    const inactieveMw = medewerkers.filter(m => m.Status === 'inactief' || m.Status === 'uitdienst');
    const alleMwIds = new Set(medewerkers.map(m => m.Id));

    // ========== DATA INTEGRITEIT ==========

    const zonderRooster = actieveMw.filter(m => !m.Werkrooster || Object.keys(m.Werkrooster).length === 0);
    if (zonderRooster.length > 0) {
      checks.push({ cat: 'data', sev: 'error', titel: `${zonderRooster.length} medewerker(s) zonder werkrooster`,
        detail: zonderRooster.map(m => m.Naam).join(', '),
        suggestie: 'Zonder rooster worden verlofuren niet correct berekend. Vul in of kopieer van een collega met hetzelfde schema.',
        actie: 'werkrooster' });
    }

    const zonderEmail = actieveMw.filter(m => !m.Email);
    if (zonderEmail.length > 0) {
      checks.push({ cat: 'data', sev: 'warning', titel: `${zonderEmail.length} medewerker(s) zonder e-mailadres`,
        detail: zonderEmail.map(m => m.Naam).join(', '),
        suggestie: 'E-mail is vereist voor inloggen.' });
    }

    const weesVerlof = verlofaanvragen.filter(v => !alleMwIds.has(v.medewerkerId));
    if (weesVerlof.length > 0) {
      checks.push({ cat: 'data', sev: 'error', titel: `${weesVerlof.length} wees-verlofaanvra(a)g(en)`,
        detail: 'Records verwijzen naar verwijderde medewerkers.',
        suggestie: 'Opruimen via Backup exporteren → handmatig verwijderen in Firebase Console.' });
    }

    const weesOveruren = overuren.filter(o => !alleMwIds.has(o.medewerkerId));
    if (weesOveruren.length > 0) {
      checks.push({ cat: 'data', sev: 'warning', titel: `${weesOveruren.length} wees-overurenregistratie(s)`,
        suggestie: 'Overuren gekoppeld aan verwijderde medewerkers. Verstoren geen saldi maar nemen ruimte in.' });
    }

    const nulUren = verlofaanvragen.filter(v => v.Uren === 0 && v.Status !== 'ingetrokken' && v.Status !== 'afgekeurd' && v.Type !== 'ziek');
    if (nulUren.length > 0) {
      checks.push({ cat: 'data', sev: 'warning', titel: `${nulUren.length} verlofaanvra(a)g(en) met 0 uren`,
        detail: nulUren.slice(0, 5).map(v => `${v.MedewerkerNaam}: ${v.Type} (${v.StartDatum})`).join('; '),
        suggestie: 'Kan duiden op verlof op een vrije dag of ontbrekend werkrooster.' });
    }

    const langZiek = verlofaanvragen.filter(v => v.isOpenZiek && v.StartDatum).filter(v => {
      return (now - new Date(v.StartDatum + 'T12:00:00')) / 86400000 > 42;
    });
    if (langZiek.length > 0) {
      checks.push({ cat: 'data', sev: 'warning', titel: `${langZiek.length} open ziekmelding(en) > 6 weken`,
        detail: langZiek.map(v => `${v.MedewerkerNaam} (sinds ${v.StartDatum})`).join('; '),
        suggestie: 'Na 6 weken: verplichte probleemanalyse. Na 8 weken: plan van aanpak. Neem contact op met bedrijfsarts.' });
    }

    const oudeIngediend = verlofaanvragen.filter(v => v.Status === 'ingediend' && v.EindDatum && v.EindDatum < today && v.Type !== 'ziek');
    if (oudeIngediend.length > 0) {
      checks.push({ cat: 'data', sev: 'warning', titel: `${oudeIngediend.length} verlofaanvra(a)g(en) in het verleden nog niet goedgekeurd`,
        detail: oudeIngediend.slice(0, 5).map(v => `${v.MedewerkerNaam}: ${v.Type} (${v.StartDatum})`).join('; '),
        suggestie: 'Verstoort saldo-berekeningen. Keur goed of af.' });
    }

    const oudeOveruren = overuren.filter(o => o.Status === 'ingediend').filter(o => {
      if (!o.ingediendOp) return true;
      return (now - new Date(o.ingediendOp)) / 86400000 > 14;
    });
    if (oudeOveruren.length > 0) {
      checks.push({ cat: 'data', sev: 'warning', titel: `${oudeOveruren.length} overurenregistratie(s) > 14 dagen niet goedgekeurd`,
        detail: oudeOveruren.slice(0, 5).map(o => `${o.MedewerkerNaam}: ${o.Uren}u (${o.Datum})`).join('; '),
        suggestie: 'Behandel openstaande overuren tijdig.' });
    }

    // Duplicaten
    const verlofDupeMap = {};
    let dupes = 0;
    verlofaanvragen.filter(v => v.Status !== 'ingetrokken' && v.Status !== 'afgekeurd').forEach(v => {
      const key = `${v.medewerkerId}-${v.Type}-${v.StartDatum}-${v.EindDatum}`;
      if (verlofDupeMap[key]) dupes++;
      verlofDupeMap[key] = true;
    });
    if (dupes > 0) {
      checks.push({ cat: 'data', sev: 'warning', titel: `${dupes} mogelijke dubbele verlofaanvra(a)g(en)`,
        suggestie: 'Controleer in Verlof overzicht en trek duplicaten in.' });
    }

    // Overlappend verlof
    let overlaps = 0;
    for (const mw of actieveMw) {
      const mwVerlof = verlofaanvragen
        .filter(v => v.medewerkerId === mw.Id && v.Status !== 'ingetrokken' && v.Status !== 'afgekeurd' && v.Type !== 'ziek')
        .sort((a, b) => (a.StartDatum || '').localeCompare(b.StartDatum || ''));
      for (let j = 0; j < mwVerlof.length - 1; j++) {
        if (mwVerlof[j].EindDatum >= mwVerlof[j + 1].StartDatum && mwVerlof[j].StartDatum !== mwVerlof[j + 1].StartDatum) overlaps++;
      }
    }
    if (overlaps > 0) {
      checks.push({ cat: 'data', sev: 'warning', titel: `${overlaps} overlappende verlofperiode(s)`,
        suggestie: 'Overlappende aanvragen kunnen dubbel uren aftrekken. Controleer per medewerker.' });
    }

    // Werkrooster vs contracturen
    const roosterMismatch = [];
    for (const mw of actieveMw.filter(m => m.Werkrooster && m.ContractUren)) {
      const testDatum = new Date();
      const dow = testDatum.getDay();
      const maandag = new Date(testDatum);
      maandag.setDate(maandag.getDate() - (dow === 0 ? 6 : dow - 1));
      let roosterUren = 0;
      for (let d = 0; d < 5; d++) {
        const dt = new Date(maandag);
        dt.setDate(dt.getDate() + d);
        roosterUren += getWerkUren(mw.Werkrooster, dt.toISOString().split('T')[0]);
      }
      if (Math.abs(roosterUren - mw.ContractUren) > 1) {
        roosterMismatch.push({ naam: mw.Naam, rooster: roosterUren.toFixed(1), contract: mw.ContractUren });
      }
    }
    if (roosterMismatch.length > 0) {
      checks.push({ cat: 'data', sev: 'warning', titel: `${roosterMismatch.length} medewerker(s) werkrooster ≠ contracturen`,
        detail: roosterMismatch.slice(0, 5).map(m => `${m.naam}: rooster ${m.rooster}u vs contract ${m.contract}u`).join('; '),
        suggestie: 'Verschil kan leiden tot verkeerde verlofberekeningen. Controleer werkrooster of contracturen.' });
    }

    // ========== CONFIGURATIE ==========

    const feestdagenDitJaar = feestdagen.filter(f => (f.Datum || f.datum || '').startsWith(String(currentYear)));
    if (feestdagenDitJaar.length < 8) {
      const verwacht = generateFeestdagen(currentYear);
      const bestaandeDatums = new Set(feestdagenDitJaar.map(f => f.Datum || f.datum));
      const ontbrekend = verwacht.filter(v => !bestaandeDatums.has(v.datum));
      checks.push({ cat: 'config', sev: 'warning', titel: `Slechts ${feestdagenDitJaar.length} feestdagen voor ${currentYear} (verwacht: 8+)`,
        detail: ontbrekend.length > 0 ? `Ontbrekend: ${ontbrekend.map(f => f.naam).join(', ')}` : 'Controleer handmatig',
        suggestie: ontbrekend.length > 0 ? `${ontbrekend.length} feestdagen automatisch toevoegen.` : 'Ga naar Feestdagen tab.',
        actie: 'feestdagen',
        autoFix: ontbrekend.length > 0 ? {
          label: `✨ ${ontbrekend.length} feestdagen toevoegen`,
          handler: async () => {
            for (const f of ontbrekend) await voegFeestdagToe({ naam: f.naam, datum: f.datum });
            return `${ontbrekend.length} feestdagen toegevoegd voor ${currentYear}`;
          }
        } : null });
    }

    // Feestdagen volgend jaar (vanaf okt waarschuwen)
    const volgendJaar = currentYear + 1;
    const feestdagenVJ = feestdagen.filter(f => (f.Datum || f.datum || '').startsWith(String(volgendJaar)));
    if (feestdagenVJ.length === 0 && now.getMonth() >= 9) {
      const verwacht = generateFeestdagen(volgendJaar);
      checks.push({ cat: 'config', sev: 'info', titel: `Nog geen feestdagen voor ${volgendJaar}`,
        suggestie: `Voeg feestdagen toe zodat medewerkers vooruit kunnen plannen.`,
        actie: 'feestdagen',
        autoFix: {
          label: `✨ ${verwacht.length} feestdagen ${volgendJaar}`,
          handler: async () => {
            for (const f of verwacht) await voegFeestdagToe({ naam: f.naam, datum: f.datum });
            return `${verwacht.length} feestdagen toegevoegd voor ${volgendJaar}`;
          }
        }
      });
    }

    const vakDitJaar = vakantiePerioden.filter(p => (p.startDatum || p.StartDatum || '').startsWith(String(currentYear)));
    if (vakDitJaar.length === 0) {
      checks.push({ cat: 'config', sev: 'info', titel: `Geen vakantieperiodes voor ${currentYear}`,
        suggestie: 'Schoolvakanties helpen bij verlofplanning.',
        actie: 'vakantieperioden' });
    }

    const actieveTypes = verlofTypes.filter(t => t.Actief !== false);
    if (actieveTypes.length < 2) {
      checks.push({ cat: 'config', sev: 'warning', titel: `Slechts ${actieveTypes.length} actief verloftype(s)`,
        suggestie: 'Voeg minimaal "Vakantie" en "Bijzonder verlof" toe.', actie: 'verloftypen' });
    }
    const gebruikteTypes = new Set(verlofaanvragen.map(v => v.Type));
    const ongebruikt = actieveTypes.filter(t => !gebruikteTypes.has(t.Key));
    if (ongebruikt.length > 0) {
      checks.push({ cat: 'config', sev: 'info', titel: `${ongebruikt.length} verloftype(s) nog niet gebruikt`,
        detail: ongebruikt.map(t => t.Label || t.Key).join(', '),
        suggestie: 'Overweeg ongebruikte types te deactiveren voor overzichtelijker menu.' });
    }

    const outlookStatus = getOutlookStatusSync();
    if (!outlookStatus.configured) {
      checks.push({ cat: 'config', sev: 'info', titel: 'Outlook synchronisatie niet geconfigureerd',
        suggestie: 'Koppel Outlook om werkroosters, verlof en beschikbaarheid in de gedeelde agenda te tonen. Essentieel voor het boekingssysteem.',
        actie: 'instellingen' });
    } else if (!outlookStatus.connected) {
      checks.push({ cat: 'config', sev: 'warning', titel: 'Outlook geconfigureerd maar niet verbonden',
        suggestie: 'Verbind via Instellingen → Outlook om werkrooster- en verlof-sync te activeren.',
        actie: 'instellingen' });
    } else {
      checks.push({ cat: 'config', sev: 'ok', titel: `Outlook verbonden${outlookStatus.calendarName ? ` (${outlookStatus.calendarName})` : ''}`,
        suggestie: 'Synchroniseer periodiek werkroosters. Gebruik "Wis & hersync" bij problemen.' });
    }

    // ========== SALDI & BEREKENINGEN ==========

    const negatiefSaldo = actieveMw.filter(m => {
      const totaal = m.BerekendeVerlofUren || 0;
      const opgenomen = verlofaanvragen
        .filter(v => v.medewerkerId === m.Id && v.Status === 'goedgekeurd' && v.Type !== 'ziek')
        .reduce((sum, v) => sum + (v.Uren || 0), 0);
      return opgenomen > totaal;
    });
    if (negatiefSaldo.length > 0) {
      checks.push({ cat: 'saldi', sev: 'warning', titel: `${negatiefSaldo.length} medewerker(s) met negatief verlofsaldo`,
        detail: negatiefSaldo.map(m => m.Naam).join(', '),
        suggestie: 'Controleer of overuren ingezet of contracturen aangepast moeten worden.' });
    }

    const zonderContract = actieveMw.filter(m => !m.ContractUren || m.ContractUren === 0);
    if (zonderContract.length > 0) {
      checks.push({ cat: 'saldi', sev: 'error', titel: `${zonderContract.length} medewerker(s) zonder contracturen`,
        detail: zonderContract.map(m => m.Naam).join(', '),
        suggestie: 'Zonder contracturen wordt verlofrecht niet berekend (0 recht).', actie: 'medewerkers' });
    }

    const zonderDatum = actieveMw.filter(m => !m.IndienstDatum);
    if (zonderDatum.length > 0) {
      checks.push({ cat: 'saldi', sev: 'warning', titel: `${zonderDatum.length} medewerker(s) zonder indienstdatum`,
        detail: zonderDatum.map(m => m.Naam).join(', '),
        suggestie: 'Zonder indienstdatum wordt pro-rata berekening overgeslagen.' });
    }

    // Hoog verbruik (>80%) voor okt
    const hoogVerbruik = actieveMw.filter(m => {
      const totaal = m.BerekendeVerlofUren || 0;
      if (totaal === 0) return false;
      const opgenomen = verlofaanvragen
        .filter(v => v.medewerkerId === m.Id && (v.Status === 'goedgekeurd' || v.Status === 'ingediend') && v.Type !== 'ziek')
        .reduce((sum, v) => sum + (v.Uren || 0), 0);
      return (opgenomen / totaal) > 0.8;
    });
    if (hoogVerbruik.length > 0 && now.getMonth() < 9) {
      checks.push({ cat: 'saldi', sev: 'info', titel: `${hoogVerbruik.length} medewerker(s) > 80% verlof verbruikt`,
        detail: hoogVerbruik.map(m => m.Naam).join(', '),
        suggestie: 'Weinig verlofsaldo over voor de rest van het jaar. Overweeg te bespreken.' });
    }

    // ========== BEVEILIGING ==========

    const ongehasht = medewerkers.filter(m => {
      const ww = m.wachtwoord || m.password;
      return ww && !isGehasht(ww);
    });
    if (ongehasht.length > 0) {
      checks.push({ cat: 'security', sev: 'error', titel: `${ongehasht.length} medewerker(s) met onversleuteld wachtwoord`,
        detail: ongehasht.map(m => m.Naam).join(', '),
        suggestie: 'Wachtwoorden zijn leesbaar. Hash ze nu of forceer een reset.',
        actie: 'medewerkers',
        autoFix: {
          label: `🔒 ${ongehasht.length} wachtwoorden versleutelen`,
          handler: async () => {
            let gefixt = 0;
            for (const m of ongehasht) {
              const ww = m.wachtwoord || m.password;
              const hashed = await hashWachtwoord(ww);
              await werkMedewerkerBij(m.Id, { password: hashed, wachtwoord: hashed });
              gefixt++;
            }
            return `${gefixt} wachtwoorden versleuteld (SHA-256)`;
          }
        }
      });
    }

    const zonderWw = medewerkers.filter(m => !(m.wachtwoord || m.password));
    if (zonderWw.length > 0) {
      checks.push({ cat: 'security', sev: 'error', titel: `${zonderWw.length} medewerker(s) zonder wachtwoord`,
        detail: zonderWw.map(m => m.Naam).join(', '),
        suggestie: 'Kunnen niet inloggen. Stel wachtwoord in via 🔑 knop.', actie: 'medewerkers' });
    }

    const zonderTel = actieveMw.filter(m => !m.Telefoon);
    if (zonderTel.length > 0) {
      checks.push({ cat: 'security', sev: 'info', titel: `${zonderTel.length} medewerker(s) zonder telefoonnummer`,
        detail: zonderTel.map(m => m.Naam).join(', '),
        suggestie: 'Nodig voor WhatsApp uitnodigingen.' });
    }

    const admins = medewerkers.filter(m => (m.Rollen || []).includes('admin') || m.Rol === 'admin');
    if (admins.length > 3) {
      checks.push({ cat: 'security', sev: 'info', titel: `${admins.length} admin accounts`,
        detail: admins.map(m => m.Naam).join(', '),
        suggestie: 'Veel admin accounts kan een risico zijn. Overweeg ongebruikte admin-rechten te verwijderen.' });
    }

    // ========== BESCHIKBAARHEID ==========

    const showroomMw = actieveMw.filter(m => m.ShowroomMedewerker);
    if (showroomMw.length === 0) {
      checks.push({ cat: 'beschikbaarheid', sev: 'warning', titel: 'Geen showroom-medewerkers geconfigureerd',
        suggestie: 'Markeer via Medewerkers → 🏪 Showroom. Vereist voor het boekingssysteem.',
        actie: 'medewerkers' });
    } else {
      checks.push({ cat: 'beschikbaarheid', sev: 'ok', titel: `${showroomMw.length} showroom-medewerker(s): ${showroomMw.map(m => m.Naam).join(', ')}`,
        suggestie: 'Boekingssysteem controleert beschikbaarheid real-time vóór acceptatie van afspraken.',
        actie: 'beschikbaarheid' });
    }

    const showroomIds = showroomMw.map(m => m.Id);
    const komendeDagen = [];
    for (let i = 1; i <= 14; i++) {
      const d = new Date(now); d.setDate(d.getDate() + i);
      if (d.getDay() === 0 || d.getDay() === 6) continue;
      komendeDagen.push(d.toISOString().split('T')[0]);
    }
    let geenCap = 0, laagCap = 0, minB = Infinity, minDag = '';
    for (const dag of komendeDagen) {
      const cap = checkCapaciteit(medewerkers, dag, verlofaanvragen, roosterWijzigingen, feestdagen, 2, showroomIds.length > 0 ? showroomIds : null);
      if (cap.aantalBeschikbaar === 0) geenCap++;
      else if (cap.aantalBeschikbaar < 2) laagCap++;
      if (cap.aantalBeschikbaar < minB) { minB = cap.aantalBeschikbaar; minDag = dag; }
    }
    if (geenCap > 0) {
      checks.push({ cat: 'beschikbaarheid', sev: 'error', titel: `${geenCap} dag(en) zonder bezetting komende 2 weken`,
        suggestie: 'Boekingssysteem blokkeert automatisch deze dagen voor afspraken.',
        actie: 'beschikbaarheid' });
    }
    if (laagCap > 0) {
      checks.push({ cat: 'beschikbaarheid', sev: 'warning', titel: `${laagCap} dag(en) minimale bezetting (< 2)`,
        detail: `Laagste: ${minB} op ${minDag ? new Date(minDag+'T12:00:00').toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long' }) : '?'}`,
        suggestie: 'Boekingssysteem beperkt automatisch beschikbare slots.',
        actie: 'beschikbaarheid' });
    }

    if (outlookStatus.connected) {
      checks.push({ cat: 'beschikbaarheid', sev: 'info', titel: 'Outlook werkrooster sync beschikbaar',
        suggestie: 'Synchroniseer werkroosters + verlof + mutaties naar Outlook. Gedeelde agenda als basis voor boekingssysteem.',
        actie: 'instellingen' });
    }

    // ========== ONDERHOUD ==========

    const archiefJaren = [...new Set(jaararchief.map(a => a.Jaar || a.jaar))];
    if (!archiefJaren.includes(currentYear - 1)) {
      checks.push({ cat: 'onderhoud', sev: 'info', titel: `Jaarafsluiting ${currentYear - 1} nog niet uitgevoerd`,
        suggestie: 'Archiveer saldi en zet meegenomen uren door.', actie: 'instellingen' });
    }

    if (inactieveMw.length > 0) {
      checks.push({ cat: 'onderhoud', sev: 'info', titel: `${inactieveMw.length} niet-actieve medewerker(s)`,
        detail: `${inactieveMw.filter(m => m.Status === 'inactief').length} inactief, ${inactieveMw.filter(m => m.Status === 'uitdienst').length} uit dienst`,
        suggestie: 'Uit-dienst medewerkers veilig verwijderen voor schoner systeem.' });
    }

    const oudeNotificaties = notificaties.filter(n => {
      if (!n.aangemaakt) return false;
      const d = n.aangemaakt.toDate ? n.aangemaakt.toDate() : new Date(n.aangemaakt);
      return (now - d) / 86400000 > 30;
    });
    if (oudeNotificaties.length > 20) {
      checks.push({ cat: 'onderhoud', sev: 'info', titel: `${oudeNotificaties.length} notificaties > 30 dagen`,
        suggestie: 'Veel oude notificaties vertragen laden.',
        autoFix: {
          label: '🧹 Markeer alles gelezen',
          handler: async () => { await markeerAllesGelezen(); return 'Alle notificaties als gelezen gemarkeerd'; }
        }
      });
    }

    const totaalRecords = verlofaanvragen.length + overuren.length + medewerkers.length + feestdagen.length + roosterWijzigingen.length + (flexUren?.length || 0) + (flexPlanning?.length || 0) + notificaties.length;
    checks.push({ cat: 'onderhoud', sev: totaalRecords > 10000 ? 'warning' : 'ok',
      titel: `Systeemomvang: ${totaalRecords.toLocaleString('nl-NL')} records`,
      detail: `${medewerkers.length} mw · ${verlofaanvragen.length} verlof · ${overuren.length} overuren · ${feestdagen.length} feestdagen · ${notificaties.length} notificaties`,
      suggestie: totaalRecords > 10000 ? 'Veel records — overweeg archiveren.' : 'Ruim binnen Firebase Free-tier limieten.' });

    checks.push({ cat: 'onderhoud', sev: 'info', titel: '💾 Backup herinnering',
      suggestie: 'Maak regelmatig een volledige JSON-backup via Instellingen.', actie: 'instellingen' });

    // ========== TOOLS & INTEGRATIES ==========

    if (!window.matchMedia('(display-mode: standalone)').matches) {
      checks.push({ cat: 'tools', sev: 'info', titel: 'App draait in browser (niet als PWA)',
        suggestie: 'Installeer als PWA voor push-notificaties, app-icoon en betere mobiele ervaring.' });
    }

    const boekingReady = showroomMw.length > 0 && outlookStatus.connected;
    checks.push({ cat: 'tools', sev: boekingReady ? 'ok' : 'info',
      titel: boekingReady ? '✅ Boekingssysteem gereed' : '⏳ Boekingssysteem voorbereiden',
      detail: [
        showroomMw.length > 0 ? '✅ Showroom-medewerkers' : '❌ Showroom-medewerkers',
        outlookStatus.connected ? '✅ Outlook verbonden' : '❌ Outlook niet verbonden',
        '✅ Beschikbaarheidservice actief',
        '✅ Capaciteitscheck API gereed',
        '✅ Werkrooster + mutatie overlay',
      ].join(' · '),
      suggestie: boekingReady
        ? 'Alle vereisten klaar. Het systeem controleert beschikbaarheid real-time. Bij onvoldoende capaciteit worden boekbare slots automatisch geblokkeerd.'
        : 'Configureer showroom-medewerkers en verbind Outlook om voor te bereiden.',
      actie: 'beschikbaarheid' });

    checks.push({ cat: 'tools', sev: 'info', titel: '💡 Beschikbare integraties',
      detail: [
        outlookStatus.connected ? '✅ Outlook werkrooster + verlof sync' : '💡 Outlook werkrooster + verlof sync',
        '💡 Boekingssysteem — showroombezoek planning met capaciteitscheck',
        '💡 Capaciteitswaarschuwingen in Outlook bij onderbezetting',
        '💡 API-ready beschikbaarheidsdata voor externe boekingspagina',
      ].join('\n'),
      suggestie: 'De beschikbaarheidservice levert real-time data: per medewerker, per dag, per tijdslot. Klaar voor extern boekingssysteem.' });

    // ========== ALLES OK? ==========
    if (checks.filter(c => c.sev === 'error').length === 0 && checks.filter(c => c.sev === 'warning').length === 0) {
      checks.unshift({ cat: 'status', sev: 'ok', titel: '✅ Geen kritieke problemen gevonden',
        suggestie: 'Systeem is gezond! Bekijk suggesties hieronder voor optimalisaties.' });
    }

    setResults(checks);
    setLastRun(new Date());
    setRunning(false);
    setExpandedCat({ status: true, data: true, config: true, saldi: true, security: true, beschikbaarheid: true, onderhoud: false, tools: true });
  }, [medewerkers, verlofaanvragen, overuren, feestdagen, vakantiePerioden, roosterWijzigingen, jaararchief, verlofTypes, flexUren, flexPlanning, notificaties, voegFeestdagToe, werkMedewerkerBij, markeerAllesGelezen]);

  // AI analyse prompt generator
  const generateAiPrompt = useCallback(() => {
    if (!results) return '';
    const actieveMw = medewerkers.filter(m => (m.Status || 'actief') === 'actief');
    const showroomMw = actieveMw.filter(m => m.ShowroomMedewerker);
    const outlookStatus = getOutlookStatusSync();
    const lines = [
      `# Slaaploods Verlofbeheer — Systeem Diagnose`,
      `Datum: ${new Date().toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}`,
      `Systeem: React 18 + Firebase Firestore + Vercel (PWA)`,
      ``,
      `## Systeemoverzicht`,
      `- ${medewerkers.length} medewerkers (${actieveMw.length} actief, ${showroomMw.length} showroom)`,
      `- ${verlofaanvragen.length} verlofaanvragen, ${overuren.length} overuren`,
      `- ${feestdagen.length} feestdagen, ${roosterWijzigingen.length} roosterwijzigingen`,
      `- ${verlofTypes.length} verloftypen, ${notificaties.length} notificaties`,
      `- Outlook: ${outlookStatus.connected ? 'verbonden' : outlookStatus.configured ? 'geconfigureerd' : 'niet geconfigureerd'}`,
      `- Boekingssysteem status: ${showroomMw.length > 0 && outlookStatus.connected ? 'gereed' : 'in voorbereiding'}`,
      ``,
      `## Diagnoseresultaten`,
      `- 🔴 ${results.filter(r => r.sev === 'error').length} fouten`,
      `- 🟡 ${results.filter(r => r.sev === 'warning').length} waarschuwingen`,
      `- 🔵 ${results.filter(r => r.sev === 'info').length} suggesties`,
      ``,
      ...results.filter(r => r.sev !== 'ok').map(r =>
        `### [${r.sev === 'error' ? 'FOUT' : r.sev === 'warning' ? 'WAARSCHUWING' : 'INFO'}] ${r.titel}${r.detail ? `\n${r.detail}` : ''}${r.suggestie ? `\nHuidige suggestie: ${r.suggestie}` : ''}`
      ),
      ``,
      `## Analyseer dit systeem en geef advies over:`,
      `1. De gevonden issues — zijn er extra risico's of betere oplossingen?`,
      `2. Welke moderne tools/diensten (2025-2026) verbeteren onze workflow? (planning, HR-integratie, automatisering)`,
      `3. Best practices voor een MKB met ${actieveMw.length} medewerkers`,
      `4. Boekingssysteem architectuur — klanten plannen showroombezoeken, systeem moet beschikbaarheid checken`,
      `5. Firebase Free-tier optimalisatie en schaalbaarheid`,
      `6. Outlook Graph API — werkrooster + verlof overlay optimalisatie`,
      `7. Eventuele beveiligingsverbeteringen voor een client-side React app met Firebase`,
    ];
    return lines.join('\n');
  }, [results, medewerkers, verlofaanvragen, overuren, feestdagen, roosterWijzigingen, verlofTypes, notificaties]);

  const handleCopyAi = () => {
    navigator.clipboard.writeText(generateAiPrompt()).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    });
  };

  const handleExportRapport = () => {
    if (!results) return;
    const blob = new Blob([generateAiPrompt()], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `systeem-diagnose-${new Date().toISOString().split('T')[0]}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const categories = [
    { key: 'status', label: '🏠 Status' },
    { key: 'data', label: '🗃️ Data Integriteit', desc: 'Correctheid en volledigheid' },
    { key: 'config', label: '⚙️ Configuratie', desc: 'Instellingen en koppelingen' },
    { key: 'saldi', label: '📊 Saldi & Berekeningen', desc: 'Verlof- en uurberekeningen' },
    { key: 'security', label: '🔒 Beveiliging', desc: 'Wachtwoorden en toegang' },
    { key: 'beschikbaarheid', label: '👥 Beschikbaarheid', desc: 'Bezetting & boekingssysteem' },
    { key: 'onderhoud', label: '🔧 Onderhoud', desc: 'Performance en opruimen' },
    { key: 'tools', label: '🚀 Tools & Integraties', desc: 'Moderne mogelijkheden' },
  ];

  const s = (() => {
    if (!results) return null;
    return { errors: results.filter(r => r.sev === 'error').length, warnings: results.filter(r => r.sev === 'warning').length,
      infos: results.filter(r => r.sev === 'info').length, oks: results.filter(r => r.sev === 'ok').length, total: results.length };
  })();

  const handleAutoFix = async (item, idx) => {
    if (!item.autoFix) return;
    setFixing(idx);
    try {
      const result = await item.autoFix.handler();
      alert(`✅ ${result}`);
      await runCheck();
    } catch (err) { alert(`❌ Fix mislukt: ${err.message}`); }
    finally { setFixing(null); }
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <div>
          <h3 style={{ fontSize: 16, fontWeight: 700, color: '#1a1a2e', margin: 0 }}>🩺 Systeem Diagnostiek</h3>
          <p style={{ fontSize: 12, color: '#605e5c', margin: '4px 0 0' }}>
            Controleert data, configuratie, beveiliging, beschikbaarheid, performance en boekingssysteem gereedheid.
          </p>
          {lastRun && <p style={{ fontSize: 11, color: '#a19f9d', margin: '2px 0 0' }}>Laatste check: {lastRun.toLocaleTimeString('nl-NL')} — {s?.total || 0} controles</p>}
        </div>
        <button onClick={runCheck} disabled={running}
          style={{ padding: '10px 24px', borderRadius: 8, border: 'none', background: '#0078D4', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer', opacity: running ? 0.6 : 1 }}>
          {running ? '⏳ Controleren...' : results ? '🔄 Opnieuw' : '🔍 Start controle'}
        </button>
      </div>

      {s && (
        <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
          {[
            { label: 'fouten', count: s.errors, bg: s.errors > 0 ? '#fde7e9' : '#dff6dd', color: s.errors > 0 ? '#D13438' : '#107C10', icon: s.errors > 0 ? '🔴' : '🟢' },
            { label: 'waarschuwingen', count: s.warnings, bg: s.warnings > 0 ? '#fff4ce' : '#dff6dd', color: s.warnings > 0 ? '#7A6400' : '#107C10', icon: s.warnings > 0 ? '🟡' : '🟢' },
            { label: 'suggesties', count: s.infos, bg: '#e8f4fd', color: '#0078D4', icon: '🔵' },
            { label: 'OK', count: s.oks, bg: '#dff6dd', color: '#107C10', icon: '🟢' },
          ].map(b => (
            <div key={b.label} style={{ padding: '8px 14px', borderRadius: 8, background: b.bg }}>
              <span style={{ fontSize: 16 }}>{b.icon}</span>
              <span style={{ fontSize: 13, fontWeight: 700, marginLeft: 6, color: b.color }}>{b.count} {b.label}</span>
            </div>
          ))}
        </div>
      )}

      {results && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
          <button onClick={() => setShowAiPanel(!showAiPanel)}
            style={{ padding: '8px 16px', borderRadius: 8, border: '2px solid #8764B8', background: showAiPanel ? '#8764B8' : '#f3e8ff', color: showAiPanel ? '#fff' : '#8764B8', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
            🤖 AI Analyse
          </button>
          <button onClick={handleExportRapport}
            style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #0078D4', background: '#e8f4fd', color: '#0078D4', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            📋 Exporteer rapport (.md)
          </button>
        </div>
      )}

      {showAiPanel && results && (
        <div style={{ marginBottom: 20, border: '2px solid #8764B8', borderRadius: 12, overflow: 'hidden' }}>
          <div style={{ background: '#8764B8', color: '#fff', padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
            <div>
              <span style={{ fontWeight: 700 }}>🤖 AI Analyse — Gepersonaliseerd advies</span>
              <div style={{ fontSize: 11, opacity: 0.8, marginTop: 2 }}>Kopieer en plak in Claude voor uitgebreid advies met de nieuwste tools en best practices</div>
            </div>
            <button onClick={handleCopyAi}
              style={{ padding: '8px 20px', borderRadius: 8, border: 'none', background: copied ? '#107C10' : '#fff', color: copied ? '#fff' : '#8764B8', fontSize: 13, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
              {copied ? '✅ Gekopieerd!' : '📋 Kopieer naar klembord'}
            </button>
          </div>
          <div style={{ padding: 16, background: '#faf5ff', maxHeight: 300, overflow: 'auto' }}>
            <pre style={{ fontSize: 11, color: '#323130', whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0, fontFamily: 'Consolas, monospace', lineHeight: 1.5 }}>{generateAiPrompt()}</pre>
          </div>
        </div>
      )}

      {results && categories.map(cat => {
        const items = results.filter(r => r.cat === cat.key);
        if (items.length === 0) return null;
        const isOpen = expandedCat[cat.key];
        const hasErr = items.some(i => i.sev === 'error');
        const hasWarn = items.some(i => i.sev === 'warning');
        return (
          <div key={cat.key} style={{ marginBottom: 12, border: '1px solid #edebe9', borderRadius: 10, overflow: 'hidden' }}>
            <div onClick={() => setExpandedCat(p => ({ ...p, [cat.key]: !isOpen }))}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', cursor: 'pointer',
                background: hasErr ? '#fde7e920' : hasWarn ? '#fff4ce20' : '#faf9f8' }}>
              <div>
                <span style={{ fontSize: 14, fontWeight: 700 }}>{cat.label}</span>
                {cat.desc && <span style={{ fontSize: 11, color: '#a19f9d', marginLeft: 8 }}>{cat.desc}</span>}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                {hasErr && <span style={{ fontSize: 10, background: '#D13438', color: '#fff', padding: '2px 6px', borderRadius: 4, fontWeight: 700 }}>{items.filter(i => i.sev === 'error').length}</span>}
                {hasWarn && <span style={{ fontSize: 10, background: '#7A6400', color: '#fff', padding: '2px 6px', borderRadius: 4, fontWeight: 700 }}>{items.filter(i => i.sev === 'warning').length}</span>}
                <span style={{ fontSize: 12, color: '#605e5c' }}>{items.length}</span>
                <span style={{ fontSize: 14 }}>{isOpen ? '▼' : '▶'}</span>
              </div>
            </div>
            {isOpen && (
              <div style={{ padding: '8px 16px 16px' }}>
                {items.map((item, idx) => {
                  const gIdx = results.indexOf(item);
                  return (
                    <div key={idx} style={{ padding: '10px 14px', borderRadius: 8, marginBottom: 8, background: SEV_BG[item.sev], border: `1px solid ${SEV_COLOR[item.sev]}30` }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                        <span style={{ fontSize: 16, flexShrink: 0 }}>{SEVERITY[item.sev]}</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 700, color: SEV_COLOR[item.sev] }}>{item.titel}</div>
                          {item.detail && <div style={{ fontSize: 12, color: '#605e5c', marginTop: 4, whiteSpace: 'pre-line', lineHeight: 1.4 }}>{item.detail}</div>}
                          {item.suggestie && (
                            <div style={{ fontSize: 12, color: '#323130', marginTop: 6, padding: '6px 10px', background: '#fff', borderRadius: 6, border: '1px solid #edebe9' }}>
                              💡 {item.suggestie}
                            </div>
                          )}
                          <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                            {item.autoFix && (
                              <button onClick={() => handleAutoFix(item, gIdx)} disabled={fixing === gIdx}
                                style={{ padding: '4px 12px', borderRadius: 6, border: 'none', background: '#107C10', color: '#fff', fontSize: 11, fontWeight: 700, cursor: 'pointer', opacity: fixing === gIdx ? 0.6 : 1 }}>
                                {fixing === gIdx ? '⏳ Bezig...' : item.autoFix.label}
                              </button>
                            )}
                            {item.actie && onNavigate && (
                              <button onClick={() => onNavigate(item.actie)}
                                style={{ padding: '4px 12px', borderRadius: 6, border: '1px solid #0078D4', background: '#e8f4fd', color: '#0078D4', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                                📎 Ga naar {item.actie}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      {!results && !running && (
        <div style={{ textAlign: 'center', padding: 40, color: '#a19f9d' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🩺</div>
          <p style={{ fontSize: 14 }}>Klik op <strong>"Start controle"</strong> om het systeem te diagnosticeren.</p>
          <p style={{ fontSize: 12, maxWidth: 500, margin: '8px auto 0', lineHeight: 1.5 }}>
            Controleert data, configuratie, beveiliging, saldi, beschikbaarheid, boekingssysteem gereedheid en adviseert over moderne tools.
          </p>
        </div>
      )}
    </div>
  );
}
